import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaShieldAlt,
  FaIdCard,
  FaFingerprint,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import UpiAtmService from "../../../services/UpiAtmService";
import BiometricService from "../../../services/BiometricService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";
import BiometricModal from "../../../components/Dialogs/BiometricModal"; // adjust path

// Hardcoded WADH – replace with actual value if needed
const WADH_VALUE = "";

const JioEkyc = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);
  const [aadhaarConsent, setAadhaarConsent] = useState("");
  const [formData, setFormData] = useState({
    aadhaar_number: "",
    aadhaar_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);

  const [ekycStatusData, setEkycStatusData] = useState();

  useEffect(() => {
    handleEkycStatus();
  }, []);

  /* -------------------------------
       AGENT REGISTRATION
    -------------------------------- */
  const handleAgentRegistration = async () => {
    if (loading) return;

    setLoading(true);
    try {
      //  const result = await UpiAtmService.agentRegistration();

      const result = await UpiAtmService.generateQR(
        "9836114520",
        "100",
        "22.7244",
        "88.4962",
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        Swal.fire("Success!", res.message, "success");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Network error";
      Swal.fire("Oops!", errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
        EKYC STATUS
    -------------------------------- */
  const handleEkycStatus = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const result = await UpiAtmService.ekycStatus();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setEkycStatusData(res.data);
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Network error";
      Swal.fire("Oops!", errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Location state
  const [location, setLocation] = useState({
    lat: null,
    lng: null,
    error: null,
    loading: false,
  });

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      setLocation((prev) => ({ ...prev, loading: true, error: null }));

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({
            lat: latitude,
            lng: longitude,
            error: null,
            loading: false,
          });
          resolve({ lat: latitude, lng: longitude });
        },
        (error) => {
          let errorMessage = "Unable to retrieve your location.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Location permission denied. Please enable location access in your browser settings.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable.";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out.";
              break;
            default:
              errorMessage = "An unknown error occurred.";
          }
          setLocation({
            lat: null,
            lng: null,
            error: errorMessage,
            loading: false,
          });
          reject(new Error(errorMessage));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  };

  // Handle form input changes (same as before)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;

    if (name === "aadhaar_number") {
      formattedValue = value.replace(/\D/g, "").substring(0, 12);
      if (formattedValue.length > 8) {
        formattedValue = formattedValue.replace(
          /(\d{4})(\d{4})(\d{0,4})/,
          "$1 $2 $3",
        );
      } else if (formattedValue.length > 4) {
        formattedValue = formattedValue.replace(/(\d{4})(\d{0,4})/, "$1 $2");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form (unchanged)
  const validateForm = () => {
    const newErrors = {};
    const cleanAadhaar = formData.aadhaar_number.replace(/\s/g, "");

    if (!cleanAadhaar.trim()) {
      newErrors.aadhaar_number = "Aadhaar number is required";
    } else if (cleanAadhaar.length !== 12) {
      newErrors.aadhaar_number = "Aadhaar number must be 12 digits";
    } else if (!/^\d{12}$/.test(cleanAadhaar)) {
      newErrors.aadhaar_number = "Invalid Aadhaar number format";
    }

    if (!formData.aadhaar_consent) {
      newErrors.aadhaar_consent = "You must agree to the Aadhaar consent";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle "Verify Aadhaar" click – get location first, then open biometric modal
  const handleVerifyAadhaar = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix all errors before verifying",
        timer: 2000,
      });
      return;
    }

    if (!ekycStatusData.wadh) {
      return Swal.fire(
        "Oops!",
        "Wadh value is missing. Please contact support team for assistance.",
        "error",
      );
    }

    // Get location first
    try {
      await getCurrentLocation();
      // Location obtained – open biometric modal
      setShowBiometricModal(true);
    } catch (locError) {
      Swal.fire({
        icon: "error",
        title: "Location Required",
        html: `${locError.message}<br/><br/>Please enable location services and try again.`,
        confirmButtonText: "Retry",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          handleVerifyAadhaar(e); // retry
        }
      });
    }
  };

  // Called when biometric capture succeeds
  const handleBiometricSuccess = async (captureData) => {
    setLoading(true);
    try {
      // Ensure we have location (should already be obtained)
      let lat = location.lat;
      let lng = location.lng;
      if (!lat || !lng) {
        // Fallback – try one more time
        const coords = await getCurrentLocation();
        lat = coords.lat;
        lng = coords.lng;
      }

      const xmlString = BiometricService.convertXmlToBase64(
        captureData.xmlResponse,
      );

      console.log(xmlString);
      console.log(captureData.xmlResponse);
      const aadhaar_number = formData.aadhaar_number.replace(/\s/g, "");

      const result = await UpiAtmService.ekyc(
        aadhaar_number,
        xmlString,
        lat.toString(),
        lng.toString(),
        ekycStatusData.token,
      );

      if (!result.success) {
        throw new Error(result.message || "Biometric verification failed");
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "E-KYC Successful",
          text: res.message || "Agent E-KYC Successful",
        }).then(() => {
          navigate("/bc-agent-onboarding");
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Biometric Verification Failed",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/bc-agent-onboarding");
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Jio Payments Bank E-KYC</h5>
                <Button
                  variant="outline-secondary"
                  onClick={handleGoBack}
                  size="sm"
                >
                  <FaArrowLeft className="me-1" />
                  Back
                </Button>
              </div>

              <div className="pb-3">
                <Button
                  variant="secondary"
                  onClick={handleAgentRegistration}
                  size="sm"
                >
                  Agent Registration
                </Button>
              </div>

              <Form onSubmit={handleVerifyAadhaar}>
                {/* Aadhaar Number Input */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>
                      <FaUser className="me-2" />
                      Aadhaar Number
                    </strong>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaIdCard />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="aadhaar_number"
                      placeholder="Enter 12-digit Aadhaar number"
                      value={formData.aadhaar_number}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_number}
                      maxLength="14"
                    />
                  </InputGroup>
                  {errors.aadhaar_number && (
                    <Form.Text className="text-danger">
                      {errors.aadhaar_number}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Enter your 12-digit Aadhaar number (e.g., 1234 5678 9012)
                  </Form.Text>
                </Form.Group>

                {/* Aadhaar Consent Checkbox */}
                <Form.Group className="mb-4">
                  <div className="border rounded-3 p-3 bg-light">
                    <Form.Check
                      type="checkbox"
                      id="aadhaar-consent"
                      label={
                        <span>
                          I hereby give my consent for Aadhaar verification as
                          per the{" "}
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none btn-sm fw-bold"
                            onClick={() => setShowConsentModal(true)}
                          >
                            Aadhaar Consent
                          </Button>
                        </span>
                      }
                      name="aadhaar_consent"
                      checked={formData.aadhaar_consent}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_consent}
                    />
                    {errors.aadhaar_consent && (
                      <Alert variant="danger" className="mt-2 py-2">
                        <FaExclamationTriangle className="me-2" />
                        {errors.aadhaar_consent}
                      </Alert>
                    )}
                  </div>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="py-3 fw-bold rounded-3 shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaFingerprint className="me-2" />
                        Capture Fingerprint
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Row>

      {/* Aadhaar Consent Modal (unchanged) */}
      <Modal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2" />
            Aadhaar Consent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="border p-3 bg-light rounded">
            <h6 className="fw-bold mb-2">Aadhaar Consent Declaration</h6>

            <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
              I hereby give my consent and have no objection to using my Aadhaar
              details for the purpose of identity verification (E-KYC). I
              voluntarily authorize the service provider to access and use my
              Aadhaar information as per UIDAI guidelines for authentication. I
              understand that my information will be used only for verification
              purposes and will not be misused or shared without my consent.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConsentModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setFormData((prev) => ({ ...prev, aadhaar_consent: true }));
              setShowConsentModal(false);
            }}
          >
            <FaCheckCircle className="me-1" />I Agree
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Biometric Modal */}
      <BiometricModal
        open={showBiometricModal}
        onClose={() => setShowBiometricModal(false)}
        onSuccess={handleBiometricSuccess}
        aadhaarNumber={formData.aadhaar_number.replace(/\s/g, "")}
        wadh={ekycStatusData?.wadh || ""}
      />
    </Container>
  );
};

export default JioEkyc;
